import React, { type ChangeEvent, type MouseEvent, useState } from 'react';
import { Logger } from '@saviynt/common';
import { Box, Checkbox, Icon, InlineMessage } from '@saviynt/design-system';
import cx from 'classnames';

import {
  ExternalLinkWarningModal,
  FieldLabel,
} from '../../../../shared/components';
import { testIds, TNC_TYPES, VALIDATION_TYPES } from '../../../../shared/constants';
import { type TermsAndConditionsField } from '../../../../shared/types';
import { buildTnCMessageNodes } from '../../../../shared/utils';
import TermsAndConditionsModal from '../../../FormBuilder/components/Modals/TermsAndConditionsModal/TermsAndConditionsModal';
import {
  PREVIEW_BANNERS,
  type PreviewBannerType,
} from '../LocalPreviewAlerts/LocalPreviewAlerts';

import styles from './PreviewFields.module.css';

interface TermsAndConditionsProps {
  field: TermsAndConditionsField;
  isChecked: boolean;
  onChange?: (value: boolean) => void;
  error?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
  showBanner?: (type: PreviewBannerType, message?: string) => void;
}

function TermsAndConditions({
  field,
  isChecked,
  onChange,
  error,
  isRequired: isRequiredProp,
  isReadOnly: isReadOnlyProp,
  showBanner,
}: TermsAndConditionsProps) {
  const [hasAcceptedTextTerms, setHasAcceptedTextTerms] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isExternalLinkModalOpen, setIsExternalLinkModalOpen] = useState(false);

  const {
    id,
    label,
    infoTooltipText,
    isReadOnly,
    advConfig: { richText, tncMessage, tncType, isReadingEnforced, linkUrl },
    validations,
  } = field;

  // Use prop if provided, otherwise fallback to validation rules
  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;

  const isTnCTypeText = tncType === TNC_TYPES.TEXT;
  const isReadOnlyResult = isReadOnly || isReadOnlyProp || false;
  const checkboxLabel = (tncMessage || '').replace(/[{}]/g, '');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (isReadingEnforced && !hasAcceptedTextTerms && isTnCTypeText) {
      if (
        event &&
        'preventDefault' in event &&
        typeof event.preventDefault === 'function'
      ) {
        event.preventDefault();
      }

      setIsModalOpen(true);

      return;
    }

    onChange?.(event.target.checked);
  };

  const handleLinkClick = (event: MouseEvent) => {
    event.stopPropagation();

    if (isTnCTypeText) {
      setIsModalOpen(true);

      return;
    }

    if (tncType === TNC_TYPES.URL && !linkUrl) {
      showBanner?.(PREVIEW_BANNERS.TERMS_LINK_EMPTY);
    }

    if (tncType === TNC_TYPES.URL && linkUrl) {
      setIsExternalLinkModalOpen(true);

      return;
    }

    Logger.error(`Invalid term type: ${tncType}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
  const tncMessageNodes = buildTnCMessageNodes(tncMessage, handleLinkClick);

  return (
    <>
      <Box className={styles.termsAndConditions}>
        <FieldLabel
          label={label}
          isRequired={isRequired}
          isReadOnly={isReadOnly}
          infoTooltipText={infoTooltipText}
          shouldRequiredAsterisk={isRequired && !isChecked}
        />
        <Checkbox
          id={`${id}-terms-and-conditions`}
          kind='singleLine'
          textPosition='after'
          ariaLabel={checkboxLabel}
          label={
            <Box className={styles.termsAndConditionsTermsTextWrapper}>
              {tncMessageNodes}
            </Box>
          }
          labelLineLimit={4} // Max char count is 150
          onChange={handleChange}
          isChecked={isChecked}
          isDisabled={isReadOnlyResult}
          isCritical={!!error}
          // TODO: Needs DS update, HelperText will be renamed to the more accurate CriticalHelperText
          HelperText={
            error && (
              <InlineMessage
                text={error}
                colorTheme='critical'
                size='small'
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
            )
          }
          className={styles.termsAndConditionsCheckbox}
          classNames={{
            labelClassName: cx(isReadOnlyResult && styles.value__isReadOnly),
          }}
        />
      </Box>
      <TermsAndConditionsModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        onAccept={() => {
          setHasAcceptedTextTerms(true);
          setIsModalOpen(false);
          onChange?.(true);
        }}
        richText={richText}
      />
      <ExternalLinkWarningModal
        isOpen={isExternalLinkModalOpen}
        onClose={() => {
          setIsExternalLinkModalOpen(false);
        }}
        onContinue={() => {
          setIsExternalLinkModalOpen(false);
          window.open(linkUrl, '_blank', 'noopener,noreferrer');
        }}
        testIds={{
          modal: testIds.FF_TNC_EXTERNAL_LINK_MODAL,
          continueButton: testIds.FF_TNC_EXTERNAL_LINK_CONTINUE_BUTTON,
          cancelButton: testIds.FF_TNC_EXTERNAL_LINK_CANCEL_BUTTON,
        }}
      />
    </>
  );
}

export default TermsAndConditions;
